import { z } from 'zod';
// mag-campaign-proposer — spec §0b.7. Output schema for the proposer
// agent that turns a brand's recent inputs + brain context into a
// bounded campaign proposal the founder reviews. Lands as a feed_card
// with kind='mag_campaign_proposal'; accept → spawn a real campaign,
// dismiss → record a trust signal.
export const magCampaignProposerKindSchema = z.enum(['paid_push', 'content_push', 'hybrid']);
/** One deliverable mag would ship as part of the campaign. Free-form
 *  shape on purpose — the agent describes intent; the campaign-draft
 *  pipeline materializes the concrete deliverables once the founder
 *  accepts. Kind is loose; the proposer can name "meta_paid_ad" /
 *  "instagram_post" / "google_search_ad" / "carousel" / "reel" / etc. */
export const magCampaignProposerDeliverableSchema = z.object({
    /** Loose tag — what kind of deliverable. */
    kind: z.string().min(1).max(60),
    /** One-line mag-voice brief describing what this deliverable does.
     *  Lowercase, "i" not "we", concrete. */
    brief: z.string().min(1).max(280),
});
export const magCampaignProposerProposalSchema = z.object({
    /** Short mag-voice campaign name. ("spring trip push", "bfcm
     *  pre-launch", "wizard fix v2".) */
    name: z.string().min(1).max(80),
    /** Mag's hypothesis — what she's testing + why. 1-2 sentences,
     *  lowercase. ("i think founder-face creatives will outperform
     *  product shots in the seasonal moment.")
     *
     *  400, not 280. A 2026-09-30 benchmark ran this agent six times per
     *  model against a real brand: opus produced a valid proposal 1 time
     *  in 6 and sonnet 3 in 6, and EVERY failure was this field
     *  overflowing. The runs that did pass measured 257, 257, 260 and 269
     *  characters — the old 280 ceiling sat about 4% above the middle of
     *  the output distribution, so overshooting was the normal case, not
     *  the exceptional one. Two sentences in mag's voice are simply
     *  longer than a tweet. The card that renders a hypothesis elsewhere
     *  allows 1200 (feed-card.ts strategyProposalCardPayloadSchema), so
     *  400 is still conservative for the surface. */
    hypothesis: z.string().min(1).max(400),
    /** Copy that lands in the feed card body — the founder reads this
     *  to decide. 3-5 sentences, mag voice. Cites which brand_input(s)
     *  triggered the proposal. */
    bodyForFounder: z.string().min(40).max(1200),
    kind: magCampaignProposerKindSchema,
    proposedDeliverables: z.array(magCampaignProposerDeliverableSchema).min(1).max(8),
    /** Suggested paid envelope (USD). Omit when kind === 'content_push'
     *  + paid spend isn't part of the proposal. */
    proposedPaidBudgetUsd: z.number().int().min(0).max(100000).optional(),
    /** ISO YYYY-MM-DD — the date mag wants to close the campaign +
     *  write its closing read. Used to populate
     *  campaigns.target_close_date on accept. */
    proposedTargetCloseDate: z.string(),
    /** 1-2 sentences citing the brand_input(s) that triggered this
     *  proposal. Re-emits brand-input ids verbatim for traceability.
     *  Empty array = the proposer triggered on a more diffuse signal
     *  (e.g. a memo + brain coupling). */
    triggeringBrandInputIds: z.array(z.string().uuid()).max(5),
    /** Mag's confidence on the proposal, 0-10. Low when the signal is
     *  thin and she's reaching; high when the founder explicitly asked
     *  for the push. */
    confidence: z.number().int().min(0).max(10),
});
export const magCampaignProposerOutputSchema = z.discriminatedUnion('decision', [
    z.object({
        decision: z.literal('propose'),
        proposal: magCampaignProposerProposalSchema,
    }),
    z.object({
        decision: z.literal('no_signal'),
        /** One-sentence mag-voice reason the run produced nothing
         *  actionable. Logged for ops; not shown to the founder. */
        noSignalReason: z.string().min(1).max(280),
    }),
]);
/** Card payload shape — what the feed_card stores so the dashboard
 *  + accept handler can re-hydrate the proposal without a re-run. */
export const magCampaignProposalCardPayloadSchema = z.object({
    kind: z.literal('mag_campaign_proposal'),
    proposal: magCampaignProposerProposalSchema,
    generatedAt: z.string().datetime(),
    costUsd: z.number().min(0),
});
//# sourceMappingURL=mag-campaign-proposer.js.map